import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import Anleitung from '@/screens/Anleitung';
import { Aufgaben } from '@/screens/Aufgaben';
import { AufgabenExercise } from '@/screens/AufgabenExercise';
import { AufgabenExerciseText } from '@/screens/AufgabenExercise/text';
import { DigaCodes } from '@/screens/DigaCode/DigaCodes';
import EpaScreen from '@/screens/EpaScreen';
import { Ergebnisse } from '@/screens/Ergebnisse';
import { Filler } from '@/screens/Filler';
import { Home } from '@/screens/HomePatient';
import { LicenseBooking } from '@/screens/LicenseBooking';
import PasswordChangeScreen from '@/screens/PasswordChangeScreen';
import Results from '@/screens/Results';
import PatientEinwilligungen from '@/screens/PatientEinwilligungen';
import { Icon } from '@/components/Icon';
import colors, { appTheme } from '@/config/colors';
import { Patient } from '@/stores/store.types';
import { PatientDemoProvider } from '@/contexts/PatientDemoContext';

export type PatientStackParamList = {
  Home: undefined;
  AufgabenExercise: undefined;
  AufgabenExerciseText: undefined;
  Aufgaben: {
    exerciseClass: string;
    patientId: string;
    setId: string;
    isNetworkError?: string;
    exerciseSettings: Record<string, unknown>;
    isSelf: boolean;
    selectedPatient?: Patient;
    activeSets?: unknown;
  };
  Anleitung: undefined;
  Settings: undefined;
  Einwilligungen: undefined;
  EpaScreen: undefined;
  PasswordChangeScreen: undefined;
  ResultsScreen: undefined;
  ResultDetailScreen: undefined;
  DigaCodes: undefined;
  Privacy: undefined;
  LicenseBooking: undefined;
  SignUpController: undefined;
  Login: undefined;
};

const Stack = createStackNavigator<PatientStackParamList>();

function PatientNavigationStack() {
  return (
    <Stack.Navigator
      initialRouteName={'Home'}
      screenOptions={{
        animationEnabled: false,
        gestureEnabled: false,
        headerTintColor: colors.white,
        headerStyle: { backgroundColor: appTheme.primary },
        headerLeftContainerStyle: { paddingHorizontal: 8 },
        headerRightContainerStyle: { paddingHorizontal: 16 },
        headerBackTitle: 'Zurück',
        headerMode: 'float',
        headerTitleAlign: 'center',
        headerTitleStyle: {
          fontSize: 24,
        },
      }}
    >
      <Stack.Group
        screenOptions={{
          headerStyle: {
            height: 64,
            backgroundColor: appTheme.primary,
          },
          headerBackTitleStyle: {
            fontSize: 20,
          },
          headerTitleStyle: {
            fontSize: 24,
          },
          headerBackImage: (x) =>
            Platform.OS === 'ios' ? (
              <Icon name="chevronLeft" color={x.tintColor} size={38} />
            ) : (
              <Icon name="arrowLeft" color={x.tintColor} size={38} />
            ),
        }}
      >
        <Stack.Screen
          name="Privacy"
          component={Filler}
          options={{ headerShown: false, presentation: 'modal' }}
        />

        <Stack.Screen
          name="Home"
          component={Home}
          options={{
            headerTitle: 'Übungsset auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen name="AufgabenExercise" component={AufgabenExercise} />
        <Stack.Screen
          name="AufgabenExerciseText"
          component={AufgabenExerciseText}
        />
        <Stack.Screen
          name="Aufgaben"
          component={Aufgaben}
          options={{
            title: 'Aufgaben auswählen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="Anleitung"
          component={Anleitung}
          options={{
            title: 'Anleitung',
            headerTitleAllowFontScaling: false,
          }}
        />

        <Stack.Screen
          name="Einwilligungen"
          component={PatientEinwilligungen}
          options={{
            title: 'Einwilligungen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="EpaScreen"
          component={EpaScreen}
          options={{
            title: 'Elektronische Patientenakte',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="PasswordChangeScreen"
          component={PasswordChangeScreen}
          options={{
            title: 'Passwort ändern',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultsScreen"
          component={Results}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="ResultDetailScreen"
          component={Ergebnisse}
          options={{
            title: 'Ergebnisse',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="DigaCodes"
          component={DigaCodes}
          options={{
            title: 'Rezeptcode einlösen',
            headerTitleAllowFontScaling: false,
          }}
        />
        <Stack.Screen
          name="LicenseBooking"
          component={LicenseBooking}
          options={{
            title: 'Lizenz buchen',
            headerTitleAllowFontScaling: false,
          }}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
}

export function PatientStack() {
  return (
    <PatientDemoProvider>
      <PatientNavigationStack />
    </PatientDemoProvider>
  );
}
